import { useCallback } from 'react';
import type { ColumnMetadata, ModifiedRow } from '../types';
import type { GridMetadata } from '../../menu';
import { getInitialFieldValue } from '../utils';

type UseRowOperationsProps = {
  gridData: Record<string, unknown>[];
  parsedColumns: ColumnMetadata[];
  metadata?: GridMetadata;
  inlineEditable: boolean;
  setDisplayData: React.Dispatch<React.SetStateAction<Record<string, unknown>[]>>;
  setModifiedRows: React.Dispatch<React.SetStateAction<Map<string, ModifiedRow>>>;
  setModifiedCells: React.Dispatch<React.SetStateAction<Set<string>>>;
  setMarkedForDeletion: React.Dispatch<React.SetStateAction<Set<string>>>;
  setValidationErrors: React.Dispatch<React.SetStateAction<Map<string, string>>>;
  tempIdCounterRef: React.MutableRefObject<number>;
};

type UseRowOperationsReturn = {
  handleDataUpdate: (rowIndex: number, columnId: string, value: unknown) => void;
  handleRowDataUpdate: (rowIndex: number, patch: Record<string, unknown>) => void;
  handleAddNewRow: () => void;
  handleAddNewRowWithData: (fieldData: Record<string, unknown> | Record<string, unknown>[]) => void;
  handleCancelAll: () => void;
  handleRollbackRow: (rowId: string) => void;
};

/** Handles inline cell edits, new row creation and full cancel — auto-tracks dirty state */
export const useRowOperations = ({
  gridData,
  parsedColumns,
  inlineEditable,
  setDisplayData,
  setModifiedRows,
  setModifiedCells,
  setMarkedForDeletion,
  setValidationErrors,
  tempIdCounterRef,
}: UseRowOperationsProps): UseRowOperationsReturn => {
  // Handle one or more data updates from editable cells as a single state change.
  const handleRowDataUpdate = useCallback(
    (rowIndex: number, patch: Record<string, unknown>) => {
      setDisplayData((old) =>
        old.map((row, index) => {
          if (index === rowIndex) {
            const rowId = String(row.Id || '');
            const changedFields = Object.entries(patch).filter(
              ([columnId, value]) => row[columnId] !== value
            );

            if (changedFields.length === 0) {
              // No change, don't mark as modified
              return row;
            }

            const updatedRow = { ...row, ...Object.fromEntries(changedFields) };

            // Find the original value from gridData (server data)
            const originalRow = gridData.find((r) => String(r.Id) === rowId);
            const isNew = rowId.startsWith('temp_');

            // Clear validation errors for every changed cell.
            setValidationErrors((prev) => {
              const next = new Map(prev);
              changedFields.forEach(([columnId]) => {
                next.delete(`${rowId}:${columnId}`);
              });
              return next;
            });

            setModifiedCells((previousModifiedCells) => {
              const nextModifiedCells = new Set(previousModifiedCells);

              changedFields.forEach(([columnId, value]) => {
                const originalValue = originalRow?.[columnId];
                const isRevertedToOriginal = !isNew && originalValue === value;
                const cellKey = `${rowId}:${columnId}`;
                if (isRevertedToOriginal) {
                  nextModifiedCells.delete(cellKey);
                } else {
                  nextModifiedCells.add(cellKey);
                }
              });

              setModifiedRows((previousModifiedRows) => {
                const next = new Map(previousModifiedRows);
                const existing = next.get(rowId);
                const rowCellKeys = parsedColumns.map(
                  (col) => `${rowId}:${col.field || col.dataField}`
                );
                const hasModifiedCells = rowCellKeys.some((key) => nextModifiedCells.has(key));

                if (!hasModifiedCells && !isNew) {
                  next.delete(rowId);
                } else {
                  next.set(rowId, {
                    rowId,
                    isNew: existing?.isNew || isNew,
                    data: updatedRow,
                  });
                }

                return next;
              });

              return nextModifiedCells;
            });

            return updatedRow;
          }
          return row;
        })
      );
    },
    [
      gridData,
      parsedColumns,
      setDisplayData,
      setModifiedRows,
      setModifiedCells,
      setValidationErrors,
    ]
  );

  const handleDataUpdate = useCallback(
    (rowIndex: number, columnId: string, value: unknown) => {
      handleRowDataUpdate(rowIndex, { [columnId]: value });
    },
    [handleRowDataUpdate]
  );

  const handleAddNewRow = useCallback(() => {
    if (!inlineEditable) {
      return;
    }

    const newRowId = `temp_${tempIdCounterRef.current++}`;

    const initialValues: Record<string, unknown> = { Id: newRowId };
    parsedColumns
      // Populate editable columns as before, but also columns that carry a
      // server-provided defaultValue (e.g. WorklogDate / dvt: zDefaultDate)
      // even when the current user can't edit them — those were previously
      // left empty until save instead of being filled immediately like
      // editable fields are.
      .filter((col) => col.editable || col.allowEditing || col.defaultValue !== undefined)
      .forEach((col) => {
        const fieldName = col.field || col.dataField;
        if (fieldName) {
          initialValues[fieldName] = getInitialFieldValue(col);
        }
      });

    setDisplayData((prev) => [initialValues, ...prev]);
    setModifiedRows((prev) => {
      const next = new Map(prev);
      next.set(newRowId, {
        rowId: newRowId,
        isNew: true,
        data: initialValues,
      });
      return next;
    });
  }, [inlineEditable, parsedColumns, setDisplayData, setModifiedRows, tempIdCounterRef]);

  /** Add new rows pre-filled with the given field values (used by copyRow).
   *  Accepts a single row object or an array of rows.
   *  All rows are created in a single batch state update so temp IDs are unique. */
  const handleAddNewRowWithData = useCallback(
    (fieldData: Record<string, unknown> | Record<string, unknown>[]) => {
      const items = Array.isArray(fieldData) ? fieldData : [fieldData];
      if (items.length === 0) return;

      const newRows: { id: string; data: Record<string, unknown> }[] = [];
      items.forEach((item) => {
        const newRowId = `temp_${tempIdCounterRef.current++}`;
        const initialValues: Record<string, unknown> = { Id: newRowId };

        // Copy provided field data, skipping Id/DeletedAt
        Object.entries(item).forEach(([key, value]) => {
          if (key === 'Id' || key === 'DeletedAt') return;
          initialValues[key] = value;
        });

        newRows.push({ id: newRowId, data: initialValues });
      });

      setDisplayData((prev) => [...newRows.map((r) => r.data), ...prev]);
      setModifiedRows((prev) => {
        const next = new Map(prev);
        newRows.forEach((r) => {
          next.set(r.id, { rowId: r.id, isNew: true, data: r.data });
        });
        return next;
      });
    },
    [setDisplayData, setModifiedRows, tempIdCounterRef]
  );

  const handleRollbackRow = useCallback(
    (rowId: string) => {
      const isNew = rowId.startsWith('temp_');
      if (isNew) {
        // Remove the temporary row entirely
        setDisplayData((prev) => prev.filter((r) => String(r.Id) !== rowId));
      } else {
        // Restore the row to its original server-fetched data
        const original = gridData.find((r) => String(r.Id) === rowId);
        if (original) {
          setDisplayData((prev) => prev.map((r) => (String(r.Id) === rowId ? original : r)));
        }
      }
      // Clear all tracking state for this row
      setModifiedRows((prev) => {
        const next = new Map(prev);
        next.delete(rowId);
        return next;
      });
      setModifiedCells((prev) => {
        const next = new Set(prev);
        for (const key of next) {
          if (key.startsWith(`${rowId}:`)) next.delete(key);
        }
        return next;
      });
      setMarkedForDeletion((prev) => {
        if (!prev.has(rowId)) return prev;
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
      setValidationErrors((prev) => {
        const next = new Map(prev);
        for (const key of next.keys()) {
          if (key.startsWith(`${rowId}:`)) next.delete(key);
        }
        return next;
      });
    },
    [
      gridData,
      setDisplayData,
      setModifiedRows,
      setModifiedCells,
      setMarkedForDeletion,
      setValidationErrors,
    ]
  );

  const handleCancelAll = useCallback(() => {
    // Remove new rows and restore modified rows to original data
    setDisplayData(gridData);
    setModifiedRows(new Map());
    setModifiedCells(new Set());
    setMarkedForDeletion(new Set());
    setValidationErrors(new Map());
  }, [
    gridData,
    setDisplayData,
    setModifiedRows,
    setModifiedCells,
    setMarkedForDeletion,
    setValidationErrors,
  ]);

  return {
    handleDataUpdate,
    handleRowDataUpdate,
    handleAddNewRow,
    handleAddNewRowWithData,
    handleCancelAll,
    handleRollbackRow,
  };
};
